import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, KeyRound, Loader2, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/PageHeader";
import { CompanyLogo } from "@/components/BrandMark";
import { StatusBadge } from "@/pages/admin/CompaniesPage";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";

const Panel = ({ title, children, testid }) => (
  <section className="rise-in rounded-lg border border-slate-200 bg-white p-6 shadow-sm" data-testid={testid}>
    <h2 className="mb-4 text-base font-semibold text-slate-900 md:text-lg">{title}</h2>
    {children}
  </section>
);

const EditForm = ({ c, onSaved }) => {
  const [form, setForm] = useState({ name: c.name, slug: c.slug, status: c.status });
  const [busy, setBusy] = useState(false);
  const save = async (patch = {}) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/companies/${c.id}`, { ...form, ...patch });
      setForm({ name: data.name, slug: data.slug, status: data.status });
      toast.success("Company saved");
      onSaved(data);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const active = c.status === "active";
  return (
    <Panel title="Company" testid="company-edit-panel">
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="space-y-1.5"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="company-name-input" /></div>
        <div className="space-y-1.5"><Label>Slug</Label><Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} data-testid="company-slug-input" /></div>
        <div className="space-y-1.5"><Label>Status</Label>
          <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
            <SelectTrigger data-testid="company-status-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
          </Select>
        </div>
      </div>
      <div className="mt-5 flex flex-wrap gap-2">
        <Button onClick={() => save()} disabled={busy} className="bg-teal-600 hover:bg-teal-700" data-testid="company-save-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
        <Button variant="outline" onClick={() => save({ status: active ? "inactive" : "active" })} disabled={busy}
          className={active ? "border-red-200 text-red-700 hover:bg-red-50" : ""} data-testid="company-toggle-status-button">
          {active ? "Deactivate company" : "Reactivate company"}
        </Button>
      </div>
    </Panel>
  );
};

const Admins = ({ c, onChange }) => {
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const add = async (e) => {
    e.preventDefault();
    setError("");
    try { await api.post(`/admin/companies/${c.id}/admins`, form); setForm({ name: "", email: "", password: "" }); toast.success("Admin added"); onChange(); }
    catch (err) { setError(apiError(err)); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.email}?`)) return;
    try { await api.delete(`/admin/companies/${c.id}/admins/${u.id}`); toast.success("Admin removed"); onChange(); } catch (err) { toast.error(apiError(err)); }
  };
  const reset = async (u) => {
    const password = window.prompt(`New password for ${u.email} (min 8 characters)`);
    if (!password) return;
    try { await api.post(`/admin/companies/${c.id}/admins/${u.id}/password`, { password }); toast.success("Password reset"); } catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Panel title="Company admins" testid="company-admins-panel">
      <table className="w-full text-sm" data-testid="company-admins-table">
        <tbody>
          {c.admins.map((u) => (
            <tr key={u.id} className="border-b border-slate-100" data-testid={`admin-row-${u.email}`}>
              <td className="py-2.5 font-medium text-slate-900">{u.name}</td>
              <td className="py-2.5 text-slate-600">{u.email}{u.is_super_admin && <span className="ml-2 rounded bg-slate-900 px-1.5 py-0.5 text-[10px] text-white">Super admin</span>}</td>
              <td className="py-2.5 text-right">
                <Button size="sm" variant="ghost" onClick={() => reset(u)} data-testid={`admin-reset-${u.email}`}><KeyRound className="h-4 w-4" /></Button>
                {!u.is_super_admin && <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(u)} data-testid={`admin-remove-${u.email}`}><Trash2 className="h-4 w-4" /></Button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <form onSubmit={add} className="mt-4 grid gap-3 sm:grid-cols-[1fr_1fr_1fr_auto]" noValidate>
        <Input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="add-admin-name" />
        <Input placeholder="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="add-admin-email" />
        <Input placeholder="Initial password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="add-admin-password" />
        <Button type="submit" variant="outline" data-testid="add-admin-submit">Add admin</Button>
      </form>
      {error && <p className="mt-2 text-sm text-red-600" data-testid="add-admin-error">{error}</p>}
    </Panel>
  );
};

const Overrides = ({ c, onSaved }) => {
  const set = async (key, value) => {
    try { const { data } = await api.put(`/admin/companies/${c.id}/visibility`, { overrides: { [key]: value } }); onSaved(data); toast.success("Visibility saved"); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Panel title="Public view visibility (overrides)" testid="company-visibility-panel">
      <p className="mb-3 text-xs text-slate-500">Inherit uses the global default. A Visible or Hidden override always wins.</p>
      <table className="w-full text-sm">
        <thead className="text-left text-xs uppercase tracking-wide text-slate-500"><tr><th className="py-2">Section</th><th>Global default</th><th>Override</th><th>Effective</th></tr></thead>
        <tbody>
          {c.visibility.map((v) => (
            <tr key={v.key} className="border-t border-slate-100" data-testid={`override-row-${v.key}`}>
              <td className="py-2 font-medium text-slate-900">{v.label}</td>
              <td className="text-slate-600">{v.default ? "Visible" : "Hidden"}</td>
              <td className="py-1.5">
                <Select value={v.override} onValueChange={(val) => set(v.key, val)}>
                  <SelectTrigger className="h-8 w-32" data-testid={`override-select-${v.key}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{["inherit", "visible", "hidden"].map((o) => <SelectItem key={o} value={o} data-testid={`override-option-${v.key}-${o}`}>{o[0].toUpperCase() + o.slice(1)}</SelectItem>)}</SelectContent>
                </Select>
              </td>
              <td data-testid={`override-effective-${v.key}`} className={v.effective ? "text-green-700" : "text-red-600"}>{v.effective ? "Visible" : "Hidden"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Panel>
  );
};

export default function CompanyDetailPage() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const { refreshUser } = useAuth();
  const load = useCallback(() => api.get(`/admin/companies/${id}`).then((r) => setC(r.data)).catch((e) => toast.error(apiError(e))), [id]);
  useEffect(() => { load(); }, [load]);
  if (!c) return <Loader2 className="h-5 w-5 animate-spin text-slate-400" />;
  return (
    <div className="max-w-5xl space-y-6" data-testid="company-detail-page">
      <Link to="/admin/companies" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900" data-testid="back-to-companies"><ArrowLeft className="h-3.5 w-3.5" /> Companies</Link>
      <div className="flex items-center gap-4">
        <CompanyLogo company={c} className="h-14 w-24" testid="company-detail-logo" />
        <PageHeader eyebrow="Platform" title={c.name} subtitle={<span>/{c.slug} · <StatusBadge status={c.status} testid="company-detail-status" /></span>} />
        <Link to={`/admin/companies/${id}/users`} className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50" data-testid="company-users-link"><Users className="h-4 w-4" /> Users &amp; task roles</Link>
      </div>
      <EditForm c={c} onSaved={(d) => { setC(d); refreshUser(); }} />
      <Admins c={c} onChange={load} />
      <Overrides c={c} onSaved={setC} />
    </div>
  );
}
